import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';

export interface IFormProps extends IBaseElementProps {
	useBackground?: boolean;
	isCentered?: boolean;
}

/**
 * Form
 * @block
 * @requiresHooks
 * @icon feedback
 */
const Form: React.FC<IFormProps> = ({
	className,
	classNames = [],
	children,
	useBackground = false,
	isCentered = false,
}) => {
	const ref = useRef<HTMLDivElement>(null);

	// Adds focus classes to fields wrappers
	useEffect(() => {
		let fields = ref.current?.querySelectorAll(
			'input, textarea, select',
		) as NodeListOf<HTMLInputElement>;

		const handleFocus = (event: FocusEvent) => {
			const target = event.target as HTMLInputElement;

			(event.target as HTMLElement)
				.closest('.gfield')
				.classList.add('input-focused');

			if (target.type === 'textarea' && target.value  === '') {
				// Hide cursor temporarily so there is no jump
				target.style.color =  'transparent';
				// Timeout to wait for transition to finish so can get height of large label to calculate textarea padding then reapply the color
				setTimeout(function() {
					const label = target?.parentNode?.parentNode?.firstElementChild as HTMLInputElement;
					const paddingTop = (label) ? `${label.offsetHeight + 5.6}px` : '24px';
					target.style.paddingTop =  paddingTop;
					target.style.color =  '';
				}, 300);
			}
		};

		const handleBlur = (event: FocusEvent) => {
			const target = event.target as HTMLInputElement;
			const value = target.value;
			if (target.type === 'tel') {
				if (value === '(___) ___-____' || value === '') {
					(event.target as HTMLElement)
						.closest('.gfield')
						.classList.remove('input-focused');
				}
			} else {
				if (!value) {
					(event.target as HTMLElement)
						.closest('.gfield')
						.classList.remove('input-focused');
				}
			}
		};

		const handleChange = (event: any) => {
			const target = event.target as HTMLInputElement;
			const { value } = target;
			const wrapper = target.closest('.gfield');
			if (value) {
				wrapper?.classList.add('input-filled');
			} else {
				wrapper?.classList.remove('input-filled');
			}
		};

		const handleFocusClasses = () => {

			fields = ref.current?.querySelectorAll(
				'input, textarea, select',
			) as NodeListOf<HTMLInputElement>;

			const jQuery = window['jQuery'] || undefined;
			if (jQuery !== undefined) {
				jQuery(document).on('gform_post_render', () => {
					fields &&
						fields.forEach((field) => {
							if (field.tagName.toLowerCase() === 'select') {
								field
									.closest('.gfield')
									.classList.add('gfield--select');
							}
							if (
								field.tagName.toLowerCase() === 'input' &&
								field.type === 'email'
							) {
								field
									.closest('.gfield')
									.classList.add('gfield--email');
							}
							field.addEventListener('focus', handleFocus);
							field.addEventListener('blur', handleBlur);
							field.addEventListener('change', handleChange);
							handleChange({
								target: field,
							});

							if (field.type === 'textarea' && field.value  != '') {
								// Timeout to wait for transition to finish so can get height of label to calculate textarea top padding
								setTimeout(function() {
									const label = field?.parentNode?.parentNode?.firstElementChild as HTMLInputElement;
									const paddingTop = (label) ? `${label.offsetHeight + 5.6}px` : '24px';
									field.style.paddingTop =  paddingTop;
								}, 300);
							}
						});
				});
			}
		};

		const document = window.document;
		document.addEventListener('DOMContentLoaded', handleFocusClasses);

		return () => {
			document.removeEventListener(
				'DOMContentLoaded',
				handleFocusClasses,
			);

			fields &&
				fields.forEach((field) => {
					field.removeEventListener('focus', handleFocus);
					field.removeEventListener('blur', handleBlur);
					field.removeEventListener('change', handleChange);
				});
		};
	}, []);

	return (
		<div
			className={
				className ||
				cn(
					'form-wrapper',
					useBackground && 'form-wrapper--background',
					isCentered && 'form-wrapper--centered',
					...classNames,
				)
			}
			ref={ref}
		>
			{children}
		</div>
	);
};

export default Form;
